"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const material_1 = require("@mui/material");
const react_query_1 = require("@tanstack/react-query");
const lodash_1 = __importDefault(require("lodash"));
const react_1 = require("react");
const zod_1 = require("zod");
const errorUtil_1 = require("../../../../../common/lib/errorUtil");
const appConstants_1 = require("../../../../constants/appConstants");
const strings_1 = require("../../../../constants/strings");
const uiConstants_1 = require("../../../../constants/uiConstants");
const FormLabel_1 = require("../../../../material-ui-theme/components/styled/FormLabel");
const companyQuery_1 = require("../../../../queries/companyQuery");
const currenciesQuery_1 = require("../../../../queries/currenciesQuery");
const featureQuery_1 = require("../../../../queries/featureQuery");
const paymentQuery_1 = require("../../../../queries/paymentQuery");
const teamQuery_1 = require("../../../../queries/teamQuery");
const zustandStore_1 = require("../../../../zustandStore");
const simpleButton_1 = __importDefault(require("../../../atoms/button/simpleButton"));
const toggleSetting_1 = __importDefault(require("../../../molecules/setting/toggleSetting"));
const cards_1 = require("../../../pages/companySettings/cards");
const enrollCardAside_1 = __importDefault(require("../enrollCardAside"));
const teamApprovalLimitInput_1 = __importDefault(require("./teamApprovalLimitInput"));
const teamNameInput_1 = require("./teamNameInput");
require("./teamInfo.less");
const propertyKey = appConstants_1.companyPropertyKeys.requestLimit;
const CARD_FEATURES = new Set(['ENABLE_SINGLEUSE', 'ENABLE_MULTIUSE', 'ENABLE_SUBSCRIPTIONS']);
const TeamInfo = ({ closeCreateTeamModal }) => {
    const REQUEST_LIMIT_PROPERTY = appConstants_1.teamPropertiesConstant.requestLimit;
    const cardSourceOptionsId = (0, react_1.useId)();
    const [isLoading, setIsLoading] = (0, react_1.useState)(true);
    const [cardSourceOption, setCardSourceOption] = (0, react_1.useState)({
        id: '',
    });
    const user = (0, zustandStore_1.useZustand)(state => state.user);
    const isModalOpen = (0, zustandStore_1.useZustand)(state => state.modal?.team?.isOpen);
    const teamId = (0, zustandStore_1.useZustand)(state => state.team.teamId);
    const companyCardSources = (0, zustandStore_1.useZustand)(state => state.payment?.cards ?? []);
    const teamName = (0, zustandStore_1.useZustand)(state => state.team?.teamName);
    const requestLimit = (0, zustandStore_1.useZustand)(state => state.team.teamRequestLimit);
    const isTeamSubmitting = (0, zustandStore_1.useZustand)(state => state.modal.team.isSubmitting);
    const step = (0, zustandStore_1.useZustand)(state => state.modal?.team?.step);
    const enrollCard = (0, paymentQuery_1.useEnrollCardV2Query)();
    const { data: companyPropertiesData } = (0, react_query_1.useQuery)((0, companyQuery_1.companyPropertiesQuery)(user.companyAccountId, { cardprovider: 'tsgo-cardprovider' }));
    const { setCompanyProperty, setTeamCardFeatures, setStep, setIsTeamSubmitting, setIsAddSourceCardOpen, setCurrentCardIdOnSubmit, setNotification, cardSourceReset, setTeamCardOptions, cardSourceSetErrors, setPageLoading, } = (0, zustandStore_1.useZustand)();
    const { data: features } = (0, react_query_1.useQuery)(featureQuery_1.featuresQuery);
    const { data: requestLimitData } = (0, companyQuery_1.useGetRequestLimitQuery)();
    const { data: cardSourceOptions, refetch: refetchGetCardSources } = (0, paymentQuery_1.useGetCardSourcesQuery)({ teamId });
    const isCardSourcesCollectionNonEmpty = cardSourceOptions && Array.isArray(cardSourceOptions) && cardSourceOptions?.length > 0;
    const currentCardId = (0, zustandStore_1.useZustand)(state => state.aside.addCardSource.currentCardId);
    const addCardSourceToTeam = (0, paymentQuery_1.useAddCardSourceToTeamQuery)();
    const saveTeamProperty = (0, teamQuery_1.useSaveTeamPropertyQuery)();
    const cardFeatures = (0, zustandStore_1.useZustand)(state => state.teamCard.cardFeatures);
    const areAllCardFeaturesDisabled = (0, zustandStore_1.useZustand)(state => state.teamCard.areAllCardFeaturesDisabled);
    const cardOptions = (0, zustandStore_1.useZustand)(state => state.teamCard.cardSourceOptions);
    const { data: currencies } = (0, react_query_1.useQuery)((0, currenciesQuery_1.getCurrenciesQuery)(teamId?.[0] || ''));
    (0, react_1.useEffect)(() => {
        if (!isCardSourcesCollectionNonEmpty) {
            return;
        }
        setTeamCardOptions(cardSourceOptions);
    }, [isCardSourcesCollectionNonEmpty]);
    (0, react_1.useEffect)(() => {
        if (isCardSourcesCollectionNonEmpty && !currentCardId) {
            setCurrentCardIdOnSubmit(cardSourceOptions?.[0]?.id || '');
        }
    }, [cardSourceOptions, currentCardId, isCardSourcesCollectionNonEmpty, setCurrentCardIdOnSubmit]);
    (0, react_1.useEffect)(() => {
        if (requestLimitData) {
            setCompanyProperty({
                key: propertyKey,
                value: lodash_1.default.isObject(requestLimitData) ? '' : lodash_1.default.toNumber(requestLimitData),
            });
        }
    }, [requestLimitData, setCompanyProperty]);
    const updateCardFeatures = (0, react_1.useCallback)((name, status) => {
        if (cardFeatures.length === 0) {
            return;
        }
        const newCardFeatures = cardFeatures.map((feature) => {
            if (feature.name === name) {
                return { ...feature, isEnabled: !status };
            }
            return feature;
        });
        setTeamCardFeatures(newCardFeatures);
    }, [cardFeatures]);
    const updateCardSourceOption = (0, react_1.useCallback)(event => {
        if (!cardSourceOptions || !Array.isArray(cardSourceOptions)) {
            return;
        }
        const index = cardSourceOptions.findIndex((option) => option.id === event.target.value);
        if (index !== -1) {
            setCardSourceOption(cardSourceOptions[index]);
            setCurrentCardIdOnSubmit(cardSourceOptions[index]?.id || '');
        }
    }, [cardSourceOptions]);
    const onModalOpen = (0, react_1.useCallback)(async () => {
        try {
            if (features && Object.keys(features).length > 0 && isCardSourcesCollectionNonEmpty) {
                setIsLoading(false);
            }
            if (cardFeatures.length === 0 || !teamId) {
                const mappedFeatures = lodash_1.default.map(features, (isEnabled, name) => {
                    return { isEnabled, name, label: appConstants_1.featuresConstant[name] };
                });
                const filteredFeatures = mappedFeatures.filter(feature => {
                    return CARD_FEATURES.has(feature.name);
                });
                const cardData = filteredFeatures.map(feature => {
                    return lodash_1.default.assign(feature, { isEnabled: true });
                });
                setTeamCardFeatures(cardData);
            }
        }
        catch {
            setNotification([
                {
                    type: strings_1.notificationStrings.ERROR,
                    message: globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP ? strings_1.errorStrings.DEFAULT : strings_1.messageStrings.ERROR_MESSAGE,
                },
            ]);
            closeCreateTeamModal(isModalOpen);
        }
    }, [cardFeatures.length, features, isCardSourcesCollectionNonEmpty, isModalOpen]);
    (0, react_1.useEffect)(() => {
        void (async function () {
            if (!(teamId && isTeamSubmitting)) {
                return;
            }
            const promises = [
                addCardSourceToTeam.mutate({
                    cardSourceId: currentCardId,
                    teamId,
                }),
                Promise.all(cardFeatures.map((feature) => saveTeamProperty.mutate({
                    teamId,
                    propertyKey: feature.name,
                    propertyValue: !!feature.isEnabled,
                }))),
                saveTeamProperty.mutate({
                    teamId,
                    propertyKey: REQUEST_LIMIT_PROPERTY,
                    propertyValue: requestLimit,
                }),
            ];
            try {
                await Promise.all(promises);
                setStep(step + 1);
                setIsTeamSubmitting(false);
            }
            catch {
                setNotification([
                    {
                        type: strings_1.notificationStrings.ERROR,
                        message: globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP
                            ? strings_1.errorStrings.DEFAULT
                            : strings_1.messageStrings.SAVE_TEAM_ERROR_MESSAGE,
                    },
                ]);
            }
        })();
    }, [teamId, isTeamSubmitting, currentCardId, cardFeatures, REQUEST_LIMIT_PROPERTY, requestLimit, step]);
    const validate = ({ cardSource, cardProvider }) => {
        const result = (0, cards_1.CardSourceSchema)(cardProvider).safeParse(cardSource);
        let errors = {};
        if (result.success) {
            const validCardSource = result.data;
            if (validCardSource.id &&
                companyCardSources?.find(source => source.id === validCardSource.id && source.name === validCardSource.name)) {
                return true;
            }
            if (companyCardSources?.find(source => source.name === validCardSource.name)) {
                errors = {
                    ...errors,
                    name: strings_1.cardDetailStrings.DUPLICATE_CARD_ERROR({ cardName: validCardSource.name }),
                };
            }
        }
        else {
            const treeifiedError = zod_1.z.treeifyError(result.error);
            if (treeifiedError.properties) {
                for (const [field, issue] of Object.entries(treeifiedError.properties)) {
                    errors[field] = issue.errors[0];
                }
            }
        }
        cardSourceSetErrors(errors);
        return Object.keys(errors).length === 0;
    };
    const submitCardSourceData = (0, react_1.useCallback)((formData) => {
        if (!validate({ cardSource: formData, cardProvider: companyPropertiesData?.cardprovider || '' })) {
            setPageLoading(false);
            return;
        }
        enrollCard.mutate({
            enrollData: { ...formData, billingCurrency: currencies?.[0] || 'N/A' },
            teamId,
            cardProvider: companyPropertiesData?.cardprovider || '',
        }, {
            onSuccess: data => {
                setTeamCardOptions([
                    ...cardOptions,
                    {
                        ...data.cardSource,
                        currency: (currencies && currencies.length > 0 && currencies[0]) || 'N/A',
                        teamsCount: String(0), // default
                        lastDigits: String(data.cardSource.lastDigits),
                    },
                ]);
                setCurrentCardIdOnSubmit(data?.cardSource?.id || '');
                setNotification([
                    {
                        type: strings_1.notificationStrings.SUCCESS,
                        message: strings_1.messageStrings.CREDIT_CARD_SUCCESS_MESSAGE,
                    },
                ]);
                setCardSourceOption(data.cardSource.id);
            },
            onError(error) {
                if ((0, errorUtil_1.isCardsProviderError)(error)) {
                    setNotification([
                        {
                            type: strings_1.notificationStrings.WARNING,
                            message: strings_1.messageStrings.CARD_PROVIDER_WARNING_MESSAGE,
                        },
                    ]);
                }
                else {
                    setNotification([
                        {
                            type: strings_1.notificationStrings.WARNING,
                            message: globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP
                                ? strings_1.errorStrings.DEFAULT
                                : strings_1.messageStrings.CARD_WARNING_ERROR,
                        },
                    ]);
                }
            },
            onSettled: () => {
                cardSourceReset();
                if (user) {
                    cardSourceReset(user);
                }
                setIsAddSourceCardOpen(false);
            },
        });
    }, [companyPropertiesData?.cardprovider, currencies, teamId, cardOptions, user]);
    (0, react_1.useEffect)(() => {
        if (!features || Object.keys(features).length === 0) {
            if (isLoading) {
                refetchGetCardSources();
            }
        }
        else {
            onModalOpen();
        }
    }, [features, isLoading, onModalOpen, refetchGetCardSources]);
    const getBottomTeamFeatures = () => ((0, jsx_runtime_1.jsxs)(material_1.Grid, { size: 12, children: [(0, jsx_runtime_1.jsx)(material_1.Typography, { "ts-variant": "h3", children: globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP
                    ? strings_1.teamHeaderTabsStrings.TEAM_SETTINGS_PHYSICAL_CARDS_HEADER_TAB
                    : strings_1.teamHeaderTabsStrings.TEAM_SETTINGS_SOURCE_CARDS_HEADER_TAB }), cardOptions?.length > 0 ? ((0, jsx_runtime_1.jsxs)(material_1.FormControl, { children: [(0, jsx_runtime_1.jsx)(FormLabel_1.StyledFormLabel, { children: strings_1.teamInfoStrings.SELECT_CARD_TEXT }), (0, jsx_runtime_1.jsx)(material_1.Select, { "data-testid": "selectCard", name: "selectCard", required: true, value: currentCardId || cardSourceOption?.id || '', inputProps: { placeholder: strings_1.teamInfoStrings.SELECT_CARD_TEXT }, onChange: e => updateCardSourceOption(e), children: cardOptions.map(cardOption => ((0, jsx_runtime_1.jsx)(material_1.MenuItem, { value: cardOption?.id || cardSourceOptionsId, children: cardOption.label }, cardOption?.id || cardSourceOptionsId))) })] })) : ((0, jsx_runtime_1.jsx)(material_1.Typography, { variant: "inherit", children: strings_1.teamInfoStrings.ENROLL_CARD_MESSAGE })), (0, jsx_runtime_1.jsx)(simpleButton_1.default, { dataTestId: "enrollSourceCard-btn", className: 'tst-enroll-source-card', color: isCardSourcesCollectionNonEmpty ? uiConstants_1.UI_BUTTON_SECONDARY : uiConstants_1.UI_BUTTON_PRIMARY, size: isCardSourcesCollectionNonEmpty ? 'medium' : 'large', onClick: () => setIsAddSourceCardOpen(true), disabled: !teamName, label: globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP
                    ? strings_1.teamInfoStrings.ENROLL_PHYSICAL_CARD
                    : strings_1.teamInfoStrings.ENROLL_SOURCE_CARD })] }));
    return ((0, jsx_runtime_1.jsxs)(material_1.Paper, { variant: "outlined", "ts-variant": "modal", children: [areAllCardFeaturesDisabled ? ((0, jsx_runtime_1.jsx)(material_1.Alert, { severity: "info", variant: "filled", "ts-variant": "purple-note", children: globalThis.cardProvider.IS_AMEX_PROVIDER_SETUP
                    ? strings_1.teamSettingsOverviewStrings.ERROR_CARD_OPTIONS
                    : strings_1.teamSettingsOverviewStrings.HSBC_ERROR_CARD_OPTIONS })) : (''), (0, jsx_runtime_1.jsx)(material_1.Box, { component: "form", autoComplete: "off", children: (0, jsx_runtime_1.jsxs)(material_1.Grid, { container: true, children: [(0, jsx_runtime_1.jsx)(material_1.Grid, { size: 12, children: (0, jsx_runtime_1.jsx)(material_1.Typography, { "ts-variant": "h3", children: strings_1.teamInfoStrings.BASIC_SETTINGS_TEXT }) }), (0, jsx_runtime_1.jsx)(material_1.Grid, { style: { paddingBottom: '20px' }, size: 6, children: (0, jsx_runtime_1.jsx)(teamNameInput_1.TeamNameInput, {}) }), (0, jsx_runtime_1.jsx)(material_1.Grid, { size: 12, children: (0, jsx_runtime_1.jsx)(material_1.Divider, { "ts-variant": "dotted" }) }), (0, jsx_runtime_1.jsx)(material_1.Grid, { size: 12, children: (0, jsx_runtime_1.jsx)(material_1.Typography, { "ts-variant": "h3", children: strings_1.teamInfoStrings.VIRTUAL_CARD_OPTIONS_TEXT }) }), (0, jsx_runtime_1.jsxs)(material_1.Grid, { size: 6, children: [cardFeatures?.length
                                    ? cardFeatures.map((feature) => ((0, jsx_runtime_1.jsx)(toggleSetting_1.default, { name: feature.name, label: feature.label, onValueChange: () => updateCardFeatures(feature.name, feature.isEnabled), isChecked: feature.isEnabled }, feature.name)))
                                    : '', getBottomTeamFeatures()] }), (0, jsx_runtime_1.jsx)(material_1.Grid, { size: 6, children: (0, jsx_runtime_1.jsx)(teamApprovalLimitInput_1.default, { currency: lodash_1.default.get(cardSourceOption, 'billingCurrency', currencies?.[0] || 'N/A') }) })] }) }), (0, jsx_runtime_1.jsx)(enrollCardAside_1.default, { asideTitle: globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP
                    ? strings_1.teamInfoStrings.ENROLL_PHYSICAL_CARD
                    : strings_1.teamInfoStrings.ENROLL_SOURCE_CARD, submitCardSourceData: submitCardSourceData })] }));
};
exports.default = TeamInfo;
